/**
 * NativeBridge — Unified Flutter ↔ Web communication layer.
 *
 * All native communication should go through this module:
 *   import { nativeBridge } from '../utils/NativeBridge';
 *
 *   nativeBridge.send('logout');
 *   nativeBridge.send('openFilePicker', { type: 'camera' });
 *   nativeBridge.on('imageSelected', (detail) => { ... });
 */

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/** Outbound actions the web can request from Flutter.
 *  shareFile is split into three messages (init/chunk/commit) so large
 *  payloads survive iOS WKWebView's WKScriptMessage size ceiling, which
 *  silently drops single oversized postMessage calls. */
type NativeAction =
  | 'openFilePicker'
  | 'logout'
  | 'saveFcm'
  | 'requestNotificationPermission'
  | 'shareText'
  | 'shareFile/init'
  | 'shareFile/chunk'
  | 'shareFile/commit'
  | 'saveImage'
  | 'clearBadge'
  | 'stopVisitorRing';

/** Inbound events Flutter dispatches back to the web */
type NativeEvent =
  | 'imageSelected'       // { base64: string; name: string }
  | 'nativeActionResult'  // { type: string; payload?: any }
  | 'nativeDeepLink';     // { deepLink: string; extra?: any }

interface ImageSelectedDetail {
  base64: string;
  name: string;
}

interface NativeActionResultDetail {
  type: string;
  payload?: Record<string, unknown>;
}

type EventDetailMap = {
  imageSelected: ImageSelectedDetail;
  nativeActionResult: NativeActionResultDetail;
  nativeDeepLink: any;
};

type Listener<T> = (detail: T) => void;

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

/** Returns true when running inside the Flutter WebView shell */
export function isInNativeShell(): boolean {
  return !!(
    (window as any).__APP_PLATFORM__ ||
    (window as any).ReactNativeWebView?.postMessage ||
    (window as any).WebViewChannel?.postMessage
  );
}

/**
 * Low-level postMessage to Flutter via whatever channel is available.
 * Consumers should use `nativeBridge.send()` instead.
 */
function postToFlutter(message: string): boolean {
  try {
    // Try ReactNativeWebView shim first (set by onPageStarted injection on both platforms).
    if ((window as any).ReactNativeWebView?.postMessage) {
      (window as any).ReactNativeWebView.postMessage(message);
      return true;
    }
    // Direct WebViewChannel — Android addJavascriptInterface fallback.
    if ((window as any).WebViewChannel?.postMessage) {
      (window as any).WebViewChannel.postMessage(message);
      return true;
    }
  } catch (e) {
    console.error('[NativeBridge] postToFlutter failed:', e);
  }
  return false;
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

class NativeBridgeClass {

  // ---- Outbound (Web → Flutter) -------------------------------------------

  /**
   * Send an action request to Flutter.
   *
   * @returns `true` if the message was posted, `false` if no native shell.
   */
  send(action: NativeAction, payload?: Record<string, unknown>): boolean {
    const message = JSON.stringify({ action, ...(payload ? { payload } : {}) });
    return postToFlutter(message);
  }

  // ---- Inbound (Flutter → Web) --------------------------------------------

  /**
   * Subscribe to a native event. Returns an unsubscribe function.
   *
   * Events are dispatched by Flutter as `CustomEvent` on `window`.
   */
  on<E extends NativeEvent>(
    event: E,
    listener: Listener<EventDetailMap[E]>,
  ): () => void {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      if (detail) listener(detail);
    };
    window.addEventListener(event, handler);
    return () => window.removeEventListener(event, handler);
  }

  /**
   * One-shot listener — automatically unsubscribes after the first event.
   */
  once<E extends NativeEvent>(
    event: E,
    listener: Listener<EventDetailMap[E]>,
  ): () => void {
    const unsub = this.on(event, (detail) => {
      unsub();
      listener(detail);
    });
    return unsub;
  }

  // ---- Convenience helpers ------------------------------------------------

  /** Request image from native camera or gallery. Returns a promise. */
  pickImage(type: 'camera' | 'gallery' = 'gallery'): Promise<ImageSelectedDetail | null> {
    if (!isInNativeShell()) return Promise.resolve(null);

    return new Promise((resolve) => {
      const timeout = setTimeout(() => {
        unsub();
        resolve(null); // user cancelled or timeout
      }, 120_000); // 2 min max

      const unsub = this.once('imageSelected', (detail) => {
        clearTimeout(timeout);
        resolve(detail);
      });

      this.send('openFilePicker', { type });
    });
  }

  /** Fire-and-forget logout request */
  logout(): void {
    this.send('logout');
  }

  /**
   * Clear the iOS app icon badge and remove delivered system-tray
   * notifications on Android. Safe to call when not in the native shell
   * (becomes a no-op).
   */
  clearBadge(): boolean {
    if (!isInNativeShell()) return false;
    return this.send('clearBadge');
  }

  /** Stop the visitor-at-gate ring the native shell plays in foreground. No-op outside the shell. */
  stopVisitorRing(): boolean {
    if (!isInNativeShell()) return false;
    return this.send('stopVisitorRing');
  }

  /** Request FCM token save */
  saveFcm(mobile: string): boolean {
    return this.send('saveFcm', { mobile });
  }

  /** Ask Flutter to request OS notification permission (one-shot, idempotent on the native side) */
  requestNotificationPermission(): boolean {
    return this.send('requestNotificationPermission');
  }

  /**
   * Share plain text via native share sheet (Android/iOS).
   * Returns true if delegated to native shell. Caller should handle fallback
   * (e.g. navigator.share or `whatsapp://`) when this returns false.
   */
  shareText(opts: { text: string; title?: string }): boolean {
    if (!isInNativeShell()) return false;
    return this.send('shareText', opts);
  }

  /**
   * Share any file (PNG, JPEG, PDF, doc, etc.) via the native share sheet.
   *
   * `base64Data` is the raw base64 string (no `data:...;base64,` prefix).
   * `mimeType` defaults to `application/octet-stream` if omitted — set it
   * (e.g. `image/png`, `application/pdf`) so receiving apps render correctly.
   *
   * The payload is split into 32 KB chunks across init/chunk/commit messages
   * because iOS WKWebView silently drops single postMessage calls above
   * ~256 KB. Chunking keeps each individual message well under that limit.
   * Flutter dispatches a `nativeActionResult` ack on completion or error.
   *
   * Resolves with `{ success, error? }` once the native share sheet was
   * presented (or definitively failed). Resolves with `null` when not in
   * the native shell so callers can fall back to navigator.share / download.
   */
  shareFile(opts: { base64Data: string; fileName: string; mimeType?: string; title?: string; text?: string }):
      Promise<{ success: boolean; error?: string } | null> {
    if (!isInNativeShell()) return Promise.resolve(null);

    const { base64Data, fileName, mimeType, title, text } = opts;
    if (!base64Data) return Promise.resolve({ success: false, error: 'empty_data' });

    const transferId = `sf_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
    // 32 KB matches the chunk size used by Flutter's reverse-direction
    // _streamImageToWeb helper. Each base64 char is one byte over the wire,
    // so a 32 KB chunk produces a postMessage payload of ~32 KB plus the
    // small JSON envelope — comfortably under WKWebView's drop threshold.
    const CHUNK_SIZE = 32 * 1024;
    const totalChunks = Math.ceil(base64Data.length / CHUNK_SIZE);

    return new Promise((resolve) => {
      // 60s ceiling protects against Flutter never acking (renderer crash,
      // share dialog hung, etc.) so the UI doesn't deadlock on the spinner.
      const timeout = setTimeout(() => {
        unsub();
        resolve({ success: false, error: 'timeout' });
      }, 60_000);

      const unsub = this.on('nativeActionResult', (detail) => {
        if (detail?.type !== 'shareFile') return;
        const payload = (detail.payload ?? {}) as { transferId?: string; success?: boolean; error?: string };
        if (payload.transferId !== transferId) return;
        clearTimeout(timeout);
        unsub();
        resolve({ success: !!payload.success, error: payload.error });
      });

      const initSent = this.send('shareFile/init', {
        transferId,
        fileName,
        mimeType: mimeType ?? 'application/octet-stream',
        title: title ?? '',
        text: text ?? '',
        totalChunks,
        totalSize: base64Data.length,
      });
      if (!initSent) {
        clearTimeout(timeout);
        unsub();
        resolve(null);
        return;
      }

      // Fire chunks synchronously — each is a separate postMessage so
      // WKWebView treats them as independent WKScriptMessage deliveries.
      // Synchronous send keeps ordering deterministic without sequence numbers
      // beyond `index`.
      for (let i = 0; i < totalChunks; i++) {
        const start = i * CHUNK_SIZE;
        const end = Math.min(start + CHUNK_SIZE, base64Data.length);
        const ok = this.send('shareFile/chunk', {
          transferId,
          index: i,
          data: base64Data.slice(start, end),
        });
        if (!ok) {
          clearTimeout(timeout);
          unsub();
          resolve(null);
          return;
        }
      }

      const commitSent = this.send('shareFile/commit', { transferId });
      if (!commitSent) {
        clearTimeout(timeout);
        unsub();
        resolve(null);
      }
    });
  }

  /**
   * Save an image to the device gallery (Photos on iOS, Pictures/album on Android).
   * `base64Data` is the raw base64 string (no `data:image/png;base64,` prefix).
   * Resolves with the native result, or `null` when not running in the native shell.
   */
  saveImage(opts: { base64Data: string; fileName: string; album?: string }):
      Promise<{ success: boolean; error?: string } | null> {
    if (!isInNativeShell()) return Promise.resolve(null);

    return new Promise((resolve) => {
      const timeout = setTimeout(() => {
        unsub();
        resolve({ success: false, error: 'timeout' });
      }, 30_000);

      const unsub = this.on('nativeActionResult', (detail) => {
        if (detail?.type !== 'saveImage') return;
        clearTimeout(timeout);
        unsub();
        const payload = (detail.payload ?? {}) as { success?: boolean; error?: string };
        resolve({ success: !!payload.success, error: payload.error });
      });

      const sent = this.send('saveImage', opts);
      if (!sent) {
        clearTimeout(timeout);
        unsub();
        resolve(null);
      }
    });
  }
}

export const nativeBridge = new NativeBridgeClass();
